import * as THREE from 'three';
import TWEEN from '@tweenjs/tween.js';
import {USE_LEGACY_TWEEN_GROUP} from 'components/customer/Preview3DCommon/lib/tween';
import {AnimationHandler} from 'components/customer/Preview3D/animation/entity/AnimationHandler';
import {
    OperationGroup,
    PreviewFormValues,
    NormalizeLShapeInputs,
} from 'components/customer/Preview3D/types';
import {TemplateVariable} from 'components/customer/Preview3D/usePreview3DData';
import {applyImagePath} from 'components/customer/RoomPlanner/helpers/scene';
import {Origin} from 'components/customer/Preview3DCommon/types';
import {
    addDragEffectOnClick,
    attachSegmentLineWireframeToggle,
    setSegmentLineTransparency,
} from 'components/customer/Preview3D/helpers/Three';
import {
    generateDoorPosition,
    generateDoorDrawer,
    generateApplianceDoor,
    getDoorFormValues,
    getLShapeBifoldDoorPlacement,
    getLShape3DoorPlacement,
} from 'components/customer/Preview3DCommon/values/door';

import {
    generateDrawerPosition,
    getDrawerFormValues,
} from 'components/customer/Preview3DCommon/values/drawer';
import {createReturnDoorAnimationHandler} from 'components/customer/Preview3DCommon/animations/returnDoor';

// Constants
const DOOR_PANEL_GAP = 50;
const DOOR_BEVEL_SIZE = 12;
const DEFAULT_CABINET_WIDTH = 600;
const DEFAULT_CABINET_DEPTH = 600;
const DOOR_OPEN_DEGREES = 120;
// Matches the slide distance used for flat pull-out fronts in drawerAnimationHandler.
const DOOR_PULLOUT_OPEN_OFFSET = -250;
const INITIAL_DOOR_POSE_TIMEOUT_MS = 1000;

export const addShakerDoorDefault = ({
    values,
    variables,
    isRightDoor,
    isPairDoor,
    carcaseThickness,
    exteriorThickness,
    showTexture,
    initiallyOpen,
    applyShakerDoorMaterials,
    origin = 'PRODUCT_VISUALISER',
    isCornerPairDoor = false,
    isCornerLeftDoor = false,
    isCornerRightDoor = false,
    isTopHangDoor = false,
    isLeftApplianceMainDoor = false,
    isLeftApplianceExtendedDoor = false,
    isRightApplianceMainDoor = false,
    isRightApplianceExtendedDoor = false,
    isDoorPullout = false,
}: {
    values: PreviewFormValues;
    variables: TemplateVariable;
    isRightDoor: boolean;
    isPairDoor: boolean;
    carcaseThickness: number;
    exteriorThickness: number;
    showTexture: boolean;
    initiallyOpen: boolean;
    applyShakerDoorMaterials?: (
        model: THREE.Group,
        showTexture: boolean
    ) => void;
    origin?: Origin;
    isCornerPairDoor?: boolean;
    isCornerLeftDoor?: boolean;
    isCornerRightDoor?: boolean;
    isTopHangDoor?: boolean;
    isLeftApplianceMainDoor?: boolean;
    isLeftApplianceExtendedDoor?: boolean;
    isRightApplianceMainDoor?: boolean;
    isRightApplianceExtendedDoor?: boolean;
    isDoorPullout?: boolean;
}): Promise<THREE.Group<THREE.Object3DEventMap> | null> => {
    return new Promise<THREE.Group<THREE.Object3DEventMap> | null>(
        (resolve) => {
            try {
                const formValues = getDoorFormValues(values);
                const valuesWithThickness = {
                    ...values,
                    cabinet_carc_thickness: carcaseThickness,
                    cabinet_ext_thickness: exteriorThickness,
                };
                const doorPosition = generateDoorPosition(
                    variables,
                    valuesWithThickness,
                    formValues
                );
                const doorDrawer = !values.isInnerDrawer
                    ? generateDoorDrawer(formValues)
                    : undefined;
                const applianceDoor = generateApplianceDoor(
                    variables,
                    valuesWithThickness,
                    formValues
                );

                // Get cabinet dimensions for scaling
                const cabinetWidth =
                    values.cabinet_width || DEFAULT_CABINET_WIDTH;

                const cabinetHeight =
                    doorDrawer?.doorHeight && !variables?.isWallOvenProduct
                        ? doorDrawer?.doorHeight
                        : doorPosition?.doorHeight;

                // Calculate door dimensions using helper
                let actualDoorWidth = calculateDoorWidthDefault(
                    values,
                    cabinetWidth,
                    formValues,
                    isRightDoor,
                    isPairDoor,
                    carcaseThickness
                );

                if (isCornerPairDoor) {
                    actualDoorWidth = doorPosition?.cornerPairDoorWidth;
                } else {
                    if (isCornerLeftDoor || isCornerRightDoor) {
                        actualDoorWidth =
                            doorPosition?.cornerHangDoorWidth -
                            carcaseThickness;
                    }
                }

                if (variables?.isApplianceBifoldProduct) {
                    const applianceDoor = generateApplianceDoor(
                        variables,
                        valuesWithThickness,
                        formValues
                    );
                    if (variables?.isApplianceLeftBifoldRight && isRightDoor) {
                        actualDoorWidth =
                            applianceDoor?.rightDoorWidth - carcaseThickness;
                    }

                    if (variables?.isApplianceLeftRightBifold && !isRightDoor) {
                        actualDoorWidth = applianceDoor?.leftDoorWidth;
                    }

                    if (isRightApplianceMainDoor) {
                        actualDoorWidth = applianceDoor?.bifold?.leftDoorWidth;
                    }
                    if (isRightApplianceExtendedDoor) {
                        actualDoorWidth = applianceDoor?.bifold?.rightDoorWidth;
                    }
                }

                if (isLeftApplianceMainDoor) {
                    actualDoorWidth = applianceDoor?.bifold?.leftDoorWidth;
                }

                if (isLeftApplianceExtendedDoor) {
                    actualDoorWidth = applianceDoor?.bifold?.rightDoorWidth;
                }

                if (variables?.isApplianceBifoldPair) {
                    actualDoorWidth = applianceDoor?.bifold?.leftDoorWidth / 2;
                }

                const vertRails = values.vert_mid_rail_positions;
                const horiRails = values.hori_mid_rail_positions;

                // For pair doors, the rail positions are relative to the full cabinet width,
                // but we're rendering individual doors that are half the width. So we need
                // to scale the vertical rail positions by half.
                const adjustedVertRails =
                    isPairDoor && vertRails
                        ? vertRails.map((p) => p / 2)
                        : vertRails;

                const doorModel = createShakerDoor({
                    width:
                        actualDoorWidth +
                        (isPairDoor ||
                        isLeftApplianceMainDoor ||
                        isLeftApplianceExtendedDoor ||
                        isRightApplianceExtendedDoor ||
                        isRightApplianceMainDoor
                            ? 0
                            : carcaseThickness),
                    height: cabinetHeight,
                    // Only set rows/cols when arrays are provided; when null/undefined use auto defaults
                    ...(horiRails != null
                        ? {rows: (horiRails?.length || 0) + 1}
                        : {}),
                    ...(adjustedVertRails != null
                        ? {cols: (adjustedVertRails?.length || 0) + 1}
                        : {}),
                    gap: DOOR_PANEL_GAP,
                    insetTop: Number(values.border_width_top || 60),
                    insetLeft: Number(values.border_width_left || 60),
                    insetBottom: Number(values.border_width_bottom || 60),
                    insetRight: Number(values.border_width_right || 60),
                    depth: exteriorThickness,
                    bevelSize: DOOR_BEVEL_SIZE,
                    isRightDoor,
                    gapHorizontal: Number(values.hori_height || 60),
                    gapVertical: Number(values.vert_width || 60),
                    vertMidRailPositions: adjustedVertRails ?? null,
                    horiMidRailPositions: horiRails ?? null,
                    glassSubPanels: (values as Record<string, unknown>)
                        .glassSubPanels as boolean[][] | null | undefined,
                    isTopHangDoor,
                });

                // Determine door hang direction
                const doorHang = (values as Record<string, unknown>)
                    .door_hang as number;
                const isRightHangDoor = doorHang === 1;

                // Rotate door to upright position
                doorModel.rotation.x = Math.PI / 2;

                // Calculate and apply door position using helper
                const doorXPosition = calculateDoorXPositionDefault(
                    isRightDoor,
                    isPairDoor,
                    actualDoorWidth,
                    formValues,
                    carcaseThickness,
                    variables,
                    origin
                );

                doorModel.position.x = doorXPosition;

                // Position door in front of cabinet
                const cabinetDepth =
                    values.cabinet_depth || DEFAULT_CABINET_DEPTH;

                if (origin === 'PRODUCT_VISUALISER') {
                    doorModel.position.y = -(cabinetDepth / 2);
                } else {
                    doorModel.position.y = -1;
                    doorModel.position.x += formValues.width / 2;
                }

                if (isCornerRightDoor || isCornerLeftDoor) {
                    if (origin === 'PRODUCT_VISUALISER') {
                        doorModel.position.x = isCornerRightDoor
                            ? formValues.rightWidth / 2
                            : -formValues.rightWidth / 2 + formValues.leftDepth;
                        doorModel.position.x -= carcaseThickness / 2;
                        doorModel.position.y = isCornerRightDoor
                            ? formValues.leftWidth / 2 - formValues.rightDepth
                            : -formValues.leftWidth / 2;
                    } else {
                        doorModel.position.y = isCornerRightDoor
                            ? formValues.leftWidth - formValues.rightDepth
                            : -1;
                        doorModel.position.x = isCornerRightDoor
                            ? formValues.rightWidth
                            : doorPosition?.leftCornerDoorX;
                    }

                    if (isCornerRightDoor && !isCornerPairDoor)
                        doorModel.scale.x = -1;
                }

                let baseZ: number;
                if (origin === 'PRODUCT_VISUALISER') {
                    if (variables?.isWallOvenProduct) {
                        baseZ = doorPosition?.wallOvenHeight
                            ? doorPosition?.wallOvenHeight / 2 -
                              values.cabinet_door_top / 2
                            : 0;
                    } else {
                        baseZ =
                            values.cabinet_door_bottom / 2 -
                            values.cabinet_door_top / 2;
                        if (values.cabinet_total_drawer_height) {
                            baseZ = 0 - values.cabinet_total_drawer_height / 2;
                        }
                        if (variables.isReturnProduct) {
                            if (values.cabinet_door_bottom < 0) {
                                baseZ -= values.cabinet_door_bottom / 2;
                            }
                            if (values.cabinet_door_top < 0) {
                                baseZ += values.cabinet_door_top / 2;
                            }
                        }
                    }
                } else {
                    if (variables?.isWallOvenProduct) {
                        baseZ =
                            (doorPosition?.wallOvenHeight ?? 0) +
                            cabinetHeight / 2;
                    } else {
                        baseZ =
                            values.cabinet_door_bottom +
                            (doorDrawer?.doorHeight || cabinetHeight) / 2;
                    }
                }

                // Apply base Z position
                doorModel.position.z = baseZ;

                // Adjust for top-hang doors
                if (isTopHangDoor) {
                    if (origin === 'PRODUCT_VISUALISER') {
                        doorModel.position.x =
                            values.cabinet_door_left / 2 -
                            values.cabinet_door_right / 2;
                    } else {
                        doorModel.position.x =
                            formValues.width / 2 +
                            carcaseThickness / 2 +
                            values.cabinet_door_left / 2 -
                            values.cabinet_door_right / 2;
                    }

                    doorModel.position.z += cabinetHeight / 2;
                }

                if (isCornerRightDoor || isCornerLeftDoor) {
                    doorModel.rotation.y = (45 * Math.PI) / 180;
                }

                if (isLeftApplianceMainDoor) {
                    doorModel.position.x = formValues.doorLeft;
                    if (origin == 'PRODUCT_VISUALISER') {
                        doorModel.position.x -= values.cabinet_width / 2;
                    }
                }
                if (isLeftApplianceExtendedDoor) {
                    doorModel.position.x =
                        applianceDoor?.bifold?.rightDoorLocation - 2;
                }
                if (isRightApplianceExtendedDoor) {
                    doorModel.position.x =
                        applianceDoor?.bifold?.rightDoorWidth - 2;
                }
                if (isRightApplianceMainDoor) {
                    doorModel.scale.z *= -1;
                    doorModel.rotation.y += Math.PI;
                    doorModel.position.x =
                        applianceDoor?.bifold?.rightDoorLocation +
                        applianceDoor?.bifold?.rightDoorWidth +
                        (variables.isApplianceBifoldPair ||
                        variables.isApplianceLeftRightBifold ||
                        (variables.isApplianceBifoldProduct &&
                            !variables.isApplianceLeftBifoldRight)
                            ? 0
                            : applianceDoor?.bifold?.rightDoorWidth);
                    if (origin == 'PRODUCT_VISUALISER') {
                        doorModel.position.x -= values.cabinet_width / 2;
                    }
                }

                // Apply materials
                applyShakerDoorMaterials
                    ? applyShakerDoorMaterials(doorModel, showTexture)
                    : applyShakerDoorMaterialsDefault(
                          doorModel,
                          showTexture,
                          values.cabinet_ext_image
                              ? values.cabinet_ext_image
                              : applyImagePath(values.ext_material?.image)
                      );
                applyShakerDoorLineTransparency(doorModel, showTexture);

                // Setup door metadata
                doorModel.userData.isShakerDoor = true;
                doorModel.userData.isRightHangDoor = isRightHangDoor;
                setupDoorAnimationDefault({
                    doorModel,
                    isRightDoor,
                    initiallyOpen,
                    origin,
                    variables,
                    isCornerLeftDoor,
                    isCornerRightDoor,
                    isTopHangDoor,
                    isLeftApplianceMainDoor,
                    isLeftApplianceExtendedDoor,
                    isRightApplianceMainDoor,
                    isRightApplianceExtendedDoor,
                    isDoorPullout,
                    isPairDoor,
                    carcaseThickness,
                    exteriorThickness,
                    values,
                });

                resolve(doorModel);
            } catch {
                resolve(null);
            }
        }
    );
};

export const addShakerDoorForDrawers = ({
    values,
    variables,
    carcaseThickness,
    exteriorThickness,
    showTexture,
    applyShakerDoorMaterials,
    origin = 'PRODUCT_VISUALISER',
    initiallyOpen = false,
    hasAnyDoors = false,
}: {
    values: PreviewFormValues;
    variables: TemplateVariable;
    carcaseThickness: number;
    exteriorThickness: number;
    showTexture: boolean;
    applyShakerDoorMaterials?: (
        model: THREE.Group,
        showTexture: boolean
    ) => void;
    origin?: Origin;
    initiallyOpen?: boolean;
    hasAnyDoors?: boolean;
}): Promise<THREE.Group<THREE.Object3DEventMap> | null> => {
    return new Promise<THREE.Group<THREE.Object3DEventMap> | null>(
        (resolve) => {
            try {
                const formValues = getDrawerFormValues(values);
                const valuesWithThickness = {
                    ...values,
                    cabinet_carc_thickness: carcaseThickness,
                    cabinet_ext_thickness: exteriorThickness,
                };

                const drawerPosition = generateDrawerPosition(
                    valuesWithThickness,
                    variables,
                    formValues,
                    hasAnyDoors
                );
                // Get cabinet dimensions for scaling
                const cabinetWidth =
                    drawerPosition?.drawerFaceWidth || DEFAULT_CABINET_WIDTH;
                const cabinetDepth =
                    values.cabinet_depth || DEFAULT_CABINET_DEPTH;
                const drawers = drawerPosition?.drawers ?? [];
                if (!Array.isArray(drawers) || drawers.length === 0) {
                    resolve(null);
                    return;
                }

                const vertRails = values.vert_mid_rail_positions;
                const horiRails = values.hori_mid_rail_positions;
                const adjustedVertRails = vertRails;

                const drawerDoorsGroup = new THREE.Group();
                const drawerCount = drawers.length;
                const isRoomPlanner = origin === 'ROOM_PLANNER';

                for (let i = 0; i < drawerCount; i++) {
                    const drawer = drawers[Number(i)];

                    const borderWidthTop = isRoomPlanner
                        ? values.border_width_top
                        : values.drawer_border_width_top;
                    const borderWidthBottom = isRoomPlanner
                        ? values.border_width_bottom
                        : values.drawer_border_width_bottom;
                    const borderWidthLeft = isRoomPlanner
                        ? values.border_width_left
                        : values.drawer_border_width_left;
                    const borderWidthRight = isRoomPlanner
                        ? values.border_width_right
                        : values.drawer_border_width_right;

                    const defaultInsetTop = Number(borderWidthTop || 60);
                    const defaultInsetBottom = Number(borderWidthBottom || 60);
                    const insetLeftVal = Number(borderWidthLeft || 60);
                    const insetRightVal = Number(borderWidthRight || 60);

                    let drawerHori = Number(
                        ((values as unknown as Record<string, unknown>)[
                            'drawer_hori_height'
                        ] as number | undefined) ??
                            values.hori_height ??
                            60
                    );

                    const isCollectiveFace =
                        Number(values.drawer_face_type) === 2;

                    if (Number(values.drawer_face_type) === 3) {
                        drawerHori = Number(borderWidthTop) / 2;
                    } else if (isCollectiveFace) {
                        drawerHori = 0;
                    }

                    const isFirstDrawer = i === 0;
                    const isLastDrawer = i === drawerCount - 1;

                    let insetTopVal = defaultInsetTop;
                    let insetBottomVal = defaultInsetBottom;

                    if (drawerCount > 1) {
                        insetTopVal =
                            isCollectiveFace && isFirstDrawer
                                ? defaultInsetTop
                                : drawerHori;
                        insetBottomVal = isLastDrawer
                            ? defaultInsetBottom
                            : drawerHori;
                    }

                    if (isFirstDrawer && drawerCount > 1)
                        insetBottomVal = drawerHori;

                    const doorModel = createShakerDoor({
                        width: cabinetWidth,
                        height: Number(drawer.faceHeight) || 0,
                        // Only set rows/cols when arrays are provided; when null/undefined use auto defaults
                        ...(horiRails != null
                            ? {rows: (horiRails?.length || 0) + 1}
                            : {}),
                        ...(adjustedVertRails != null
                            ? {cols: (adjustedVertRails?.length || 0) + 1}
                            : {}),
                        gap: DOOR_PANEL_GAP,
                        insetTop: insetTopVal,
                        insetLeft: insetLeftVal,
                        insetBottom: insetBottomVal,
                        insetRight: insetRightVal,
                        depth: exteriorThickness,
                        bevelSize: DOOR_BEVEL_SIZE,
                        gapHorizontal: Number(values.hori_height || 60),
                        gapVertical: Number(values.vert_width || 60),
                        vertMidRailPositions: adjustedVertRails ?? null,
                        horiMidRailPositions: horiRails ?? null,
                        glassSubPanels: (values as Record<string, unknown>)
                            .glassSubPanels as boolean[][] | null | undefined,
                        isCollectiveFace,
                    });

                    // Rotate door to upright position
                    doorModel.rotation.x = Math.PI / 2;

                    // Positioning
                    doorModel.position.z =
                        (Number(drawer.position) || 0) + drawer.faceHeight / 2;

                    if (origin === 'PRODUCT_VISUALISER') {
                        doorModel.position.x =
                            -cabinetWidth / 2 +
                            formValues.drawerLeft / 2 -
                            formValues.drawerRight / 2;
                        doorModel.position.y = -(cabinetDepth / 2);
                    } else {
                        doorModel.position.y = -1;

                        const box = new THREE.Box3().setFromObject(doorModel);
                        const centerX = box.getCenter(new THREE.Vector3()).x;

                        doorModel.position.x =
                            centerX -
                            cabinetWidth / 2 +
                            formValues.drawerLeft -
                            carcaseThickness / 2;
                    }

                    // Apply materials
                    if (applyShakerDoorMaterials) {
                        applyShakerDoorMaterials(doorModel, showTexture);
                    } else {
                        applyShakerDoorMaterialsDefault(
                            doorModel,
                            showTexture,
                            values.cabinet_ext_image
                                ? values.cabinet_ext_image
                                : applyImagePath(values.ext_material?.image)
                        );
                    }
                    applyShakerDoorLineTransparency(doorModel, showTexture);

                    setupDoorAnimationDefault({
                        doorModel,
                        isRightDoor: false,
                        initiallyOpen,
                        origin,
                        variables,
                        isCornerLeftDoor: false,
                        isCornerRightDoor: false,
                        isTopHangDoor: false,
                        isDrawerFront: true,
                        drawerIndex: i,
                    });

                    drawerDoorsGroup.add(doorModel);
                }

                drawerDoorsGroup.userData.toggleDoor = ({
                    isDoorOrDrawerOpen,
                    animationSpeed,
                }: {
                    isDoorOrDrawerOpen: boolean;
                    animationSpeed?: number;
                }) => {
                    drawerDoorsGroup.children.forEach((child) => {
                        const childGroup = child as THREE.Group;
                        if (
                            typeof childGroup.userData.toggleDoor === 'function'
                        ) {
                            (
                                childGroup.userData.toggleDoor as (params: {
                                    isDoorOrDrawerOpen: boolean;
                                    animationSpeed?: number;
                                }) => void
                            )({isDoorOrDrawerOpen, animationSpeed});
                        }
                    });
                };

                if (isRoomPlanner) {
                    drawerDoorsGroup.position.x += exteriorThickness / 2;
                }

                resolve(drawerDoorsGroup);
            } catch {
                resolve(null);
            }
        }
    );
};

export const calculateDoorWidthDefault = (
    values: PreviewFormValues,
    cabinetWidth: number,
    formValues: ReturnType<typeof getDoorFormValues>,
    isRightDoor: boolean,
    isPairDoor: boolean,
    carcaseThickness: number,
    operationGroup?: OperationGroup,
    isRightLShape?: boolean,
    isBiFoldDoor?: boolean,
    exteriorThickness?: number,
    is3Door?: boolean
): number => {
    const doorGap = formValues.doorGap || 0;
    const doorMargin = isRightDoor
        ? values.cabinet_door_right
        : values.cabinet_door_left;

    const lShapeDoorThickness = exteriorThickness ?? carcaseThickness;
    const doorLeftMargin = Number(values.cabinet_door_left || 0);
    const doorRightMargin = Number(values.cabinet_door_right || 0);

    const lShapePlacementInputs: NormalizeLShapeInputs = {
        cabinetLeftWidth: formValues.leftWidth,
        cabinetRightWidth: formValues.rightWidth,
        cabinetLeftDepth: formValues.leftDepth,
        cabinetRightDepth: formValues.rightDepth,
        cabinetHeight: 0,
        doorGap,
        doorTop: 0,
        doorBottom: 0,
        doorLeft: doorLeftMargin,
        doorRight: doorRightMargin,
        carc: carcaseThickness,
        doorThickness: lShapeDoorThickness,
        isBaseCorner: false,
    };

    if (isBiFoldDoor && is3Door && operationGroup) {
        const isBig = isRightLShape
            ? operationGroup.IsBifoldRightDoor
            : operationGroup.IsBifoldLeftDoor;
        return getLShape3DoorPlacement(
            isBig ? 'big' : 'smallInner',
            Boolean(isRightLShape),
            lShapePlacementInputs
        ).perimeterWidth;
    }

    if (isBiFoldDoor) {
        if (isRightLShape) {
            if (isPairDoor) {
                if (operationGroup.IsLeftBifoldExtension) {
                    return (
                        (formValues.rightWidth -
                            formValues.rightDepth -
                            doorGap -
                            doorMargin * 2) /
                        2
                    );
                }
            }
            // Non-extension left/right leg doors fall through to
            // getLShapeBifoldDoorPlacement below: the single source of truth
            // for L-shape door widths (each door insets by its own-side margin
            // only, so changing one margin can't open a corner gap).
        } else {
            if (isPairDoor) {
                if (operationGroup.IsRightDoor) {
                    return (
                        (formValues.rightWidth -
                            formValues.rightDepth -
                            values.cabinet_door_right * 2) /
                        2
                    );
                }
                if (operationGroup.IsBifoldLeftDoor) {
                    return (
                        formValues.leftWidth -
                        formValues.leftDepth -
                        values.cabinet_door_left
                    );
                }
                if (operationGroup.IsBifoldRightDoor) {
                    return (
                        (formValues.rightWidth -
                            formValues.rightDepth -
                            Number(values.cabinet_door_right || 0) * 2) /
                        2
                    );
                }
            }
        }

        const bifoldPlacement = getLShapeBifoldDoorPlacement(
            operationGroup,
            lShapePlacementInputs
        );
        if (bifoldPlacement) return bifoldPlacement.perimeterWidth;
    }

    if (isPairDoor) {
        return (
            (cabinetWidth -
                formValues.returnPanelWidth +
                formValues.cabinetVoidWidth) /
                2 -
            doorGap / 2 -
            Number(doorMargin || 0) +
            carcaseThickness / 2
        );
    }

    return (
        cabinetWidth -
        formValues.returnPanelWidth -
        Number(values.cabinet_door_right || 0) -
        Number(values.cabinet_door_left || 0) +
        formValues.cabinetVoidWidth
    );
};

const calculateDoorXPositionDefault = (
    isRightDoor: boolean,
    isPairDoor: boolean,
    actualDoorWidth: number,
    formValues: ReturnType<typeof getDoorFormValues>,
    carcaseThickness: number,
    variables: TemplateVariable,
    origin: Origin = 'PRODUCT_VISUALISER'
): number => {
    const doorWidthWithCarcase = (formValues.width + carcaseThickness) / 2;
    const carcaseAddition =
        origin === 'PRODUCT_VISUALISER' ? 0 : carcaseThickness / 2;

    let xPosition = 0;

    if (isPairDoor) {
        if (isRightDoor) {
            xPosition = actualDoorWidth + formValues.doorGap / 2;
        } else {
            xPosition = xPosition - actualDoorWidth - formValues.doorGap / 2;
        }

        xPosition += carcaseAddition;
    } else {
        // Single door positioning based on hang direction
        xPosition = isRightDoor
            ? doorWidthWithCarcase - formValues.doorRight
            : -doorWidthWithCarcase + formValues.doorLeft;

        xPosition += carcaseAddition;
    }

    // Handle return panel positioning
    if (variables?.isLeftReturn && variables?.isReturnProduct) {
        if (isPairDoor) {
            xPosition +=
                formValues.returnPanelWidth / 2 -
                (formValues.cabinetVoidWidth
                    ? formValues.cabinetVoidWidth / 2
                    : 0);
        } else
            xPosition =
                xPosition +
                formValues.returnPanelWidth -
                formValues.cabinetVoidWidth;
    }

    if (!variables?.isLeftReturn && variables?.isReturnProduct) {
        if (isPairDoor) {
            xPosition -=
                formValues.returnPanelWidth / 2 -
                (formValues.cabinetVoidWidth
                    ? formValues.cabinetVoidWidth / 2
                    : 0);
        } else
            xPosition =
                xPosition -
                formValues.returnPanelWidth +
                formValues.cabinetVoidWidth;
    }

    return xPosition;
};

export const applyShakerDoorMaterialsDefault = (
    model: THREE.Group,
    showTexture: boolean,
    image: string
) => {
    // Skip if no texture available
    if (!image) {
        return;
    }

    const textureLoader = new THREE.TextureLoader();

    // Load the texture
    textureLoader.load(
        image,
        (texture) => {
            // Set up texture properties
            texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
            texture.generateMipmaps = true;
            texture.minFilter = THREE.LinearMipmapLinearFilter;
            texture.magFilter = THREE.LinearFilter;

            // Rotate texture 180 degrees
            texture.rotation = Math.PI;
            texture.center = new THREE.Vector2(0.5, 0.5);

            // Flip and scale texture
            texture.offset.set(0, 0.5);
            texture.repeat.set(0.003, 0.003);

            // Apply texture to all door parts
            model.traverse((child) => {
                if (child instanceof THREE.Mesh) {
                    const mesh = child as THREE.Mesh;

                    // Create material with texture
                    const material = new THREE.MeshStandardMaterial({
                        map: texture,
                    });

                    mesh.userData.onToggleWireframe = (
                        isWireframe: boolean
                    ) => {
                        material.transparent = isWireframe;
                        material.opacity = isWireframe ? 0 : 1;
                        material.needsUpdate = true;
                    };

                    mesh.material = material;

                    if (!showTexture) {
                        material.transparent = true;
                        material.opacity = 0;
                    }
                }
            });

            texture.needsUpdate = true;
        },
        undefined,
        () => {
            // Failed to load texture
        }
    );
};

export const createShakerDoor = ({
    width = 600,
    height = 800,
    depth = 16.5,
    inset = 80,
    insetTop = undefined,
    insetLeft = undefined,
    insetBottom = undefined,
    insetRight = undefined,
    panelDepth = 10,
    bevelThickness = 5,
    bevelSize = 12,
    bevelSegments = 1,
    gap = 40,
    gapVertical = 60,
    gapHorizontal = 60,
    rows = 1,
    cols = 1,
    frameColor = 0x7e7a79,
    panelColor = 0xeeeeee,
    isRightDoor = false,
    vertMidRailPositions,
    horiMidRailPositions,
    glassSubPanels,
    isTopHangDoor = false,
    isCollectiveFace = false,
}: {
    width: number;
    height: number;
    depth?: number;
    inset?: number;
    insetTop?: number;
    insetLeft: number;
    insetBottom?: number;
    insetRight?: number;
    panelDepth?: number;
    bevelThickness?: number;
    bevelSize?: number;
    bevelSegments?: number;
    gap?: number;
    gapHorizontal?: number;
    gapVertical?: number;
    rows?: number;
    cols?: number;
    frameColor?: number;
    panelColor?: number;
    isRightDoor?: boolean;
    vertMidRailPositions?: number[] | null;
    horiMidRailPositions?: number[] | null;
    glassSubPanels?: boolean[][] | null;
    isTopHangDoor?: boolean;
    isCollectiveFace?: boolean;
}) => {
    // Create door shape with cutouts for panels
    const doorShape = new THREE.Shape();

    // Start with outer door boundary
    doorShape.moveTo(-width / 2, -height / 2);
    doorShape.lineTo(width / 2, -height / 2);
    doorShape.lineTo(width / 2, height / 2);
    doorShape.lineTo(-width / 2, height / 2);
    doorShape.lineTo(-width / 2, -height / 2);

    // Use individual inset values or fall back to general inset
    const topInset = insetTop !== undefined ? insetTop : inset;
    const leftInset = insetLeft !== undefined ? insetLeft : inset;
    const bottomInset = insetBottom !== undefined ? insetBottom : inset;
    const rightInset = insetRight !== undefined ? insetRight : inset;

    // Use individual gap values or fall back to general gap
    const horizontalGap = gapVertical !== undefined ? gapVertical : gap;
    const verticalGap = gapHorizontal !== undefined ? gapHorizontal : gap;

    // Derived usable area inside the frame (excluding frame insets)
    // Note: When explicit mid-rail positions are provided, we compute panel sizes
    // using rail widths directly. Otherwise we subtract uniform gaps for auto layout.
    const hasExplicitCols =
        Array.isArray(vertMidRailPositions) && vertMidRailPositions.length > 0;
    const hasExplicitRows =
        Array.isArray(horiMidRailPositions) && horiMidRailPositions.length > 0;

    const usableWidthAuto =
        width - leftInset - rightInset - horizontalGap * (cols - 1);
    const usableHeightAuto =
        height - topInset - bottomInset - verticalGap * (rows - 1);

    const usableWidthExplicit = width - leftInset - rightInset;
    const usableHeightExplicit = height - topInset - bottomInset;

    const panelWidth = hasExplicitCols ? 0 : usableWidthAuto / cols;
    const panelHeight = hasExplicitRows ? 0 : usableHeightAuto / rows;

    // Compute starting offsets accounting for different insets
    // const totalWidth = cols * panelWidth + (cols - 1) * gap;
    // const totalHeight = rows * panelHeight + (rows - 1) * gap;

    // Calculate starting positions based on left and bottom insets
    const xStart =
        -width / 2 + leftInset + (hasExplicitCols ? 0 : panelWidth / 2);
    const yStart =
        -height / 2 + bottomInset + (hasExplicitRows ? 0 : panelHeight / 2);

    // Helpers to compute panel centers and sizes from mid-rail positions
    const computePanelsFromRails = (
        total: number,
        rails: number[],
        railThickness: number
    ): {centers: number[]; sizes: number[]} => {
        const positions = [...rails]
            .map((p) => Math.max(0, Math.min(total, p)))
            .sort((a, b) => a - b);

        const centers: number[] = [];
        const sizes: number[] = [];

        // First panel: from 0 to first rail edge (center - thickness/2)
        const firstEnd = Math.max(0, positions[0] - railThickness / 2);
        sizes.push(Math.max(0, firstEnd - 0));
        centers.push(0 + sizes[0] / 2);

        // Middle panels
        for (let i = 1; i < positions.length; i++) {
            const prevEnd = positions[i - 1] + railThickness / 2;
            const nextStart = positions[Number(i)] - railThickness / 2;
            const size = Math.max(0, nextStart - prevEnd);
            sizes.push(size);
            centers.push(prevEnd + size / 2);
        }

        // Last panel: from last rail edge to total
        const lastStart = positions[positions.length - 1] + railThickness / 2;
        const lastSize = Math.max(0, total - lastStart);
        sizes.push(lastSize);
        centers.push(lastStart + lastSize / 2);

        return {centers, sizes};
    };

    // Prepare panel centers and sizes for X (cols) and Y (rows)
    const xPanels = hasExplicitCols
        ? computePanelsFromRails(
              // Positions provided across full mesh width; convert proportionally
              // into the usable interior width so values like width/2 map to the
              // interior center regardless of insets. Values represent rail centers.
              usableWidthExplicit,
              vertMidRailPositions.map(
                  (p) => (p / width) * usableWidthExplicit
              ),
              horizontalGap
          )
        : {
              // xStart already includes panelWidth/2 in auto mode
              centers: Array.from(
                  {length: cols},
                  (_, c) => c * (panelWidth + horizontalGap)
              ),
              sizes: Array.from({length: cols}, () => panelWidth),
          };

    const yPanels = hasExplicitRows
        ? computePanelsFromRails(
              // Positions provided across full mesh height; convert to interior space
              // by subtracting bottom inset. Values represent rail centers.
              usableHeightExplicit,
              horiMidRailPositions.map((p) => p - bottomInset),
              verticalGap
          )
        : {
              // yStart already includes panelHeight/2 in auto mode
              centers: Array.from(
                  {length: rows},
                  (_, r) => r * (panelHeight + verticalGap)
              ),
              sizes: Array.from({length: rows}, () => panelHeight),
          };

    // Helper function to map render row/col indices to glassSubPanels array indices
    // glassSubPanels is always 3x3, where indices represent: 0=top/left, 1=middle, 2=bottom/right
    // Note: rendering starts at bottom (row 0 = bottom), but glassSubPanels[0] = top, so reverse rows
    // Logic: Top row → 0, middle rows → 1, bottom row → 2
    const getGlassSubPanelRowIndex = (
        renderRow: number,
        totalRows: number
    ): number => {
        // Reverse the row because rendering row 0 = bottom, but array row 0 = top
        const reversedRow = totalRows - 1 - renderRow;

        if (totalRows === 1) {
            return 1; // Middle position for single panel
        } else if (totalRows === 2) {
            // Two rows: first (top) → 0, second (bottom) → 2
            return reversedRow === 0 ? 0 : 2;
        } else if (totalRows === 3) {
            // Three rows: direct mapping 0→0, 1→1, 2→2
            return reversedRow;
        } else {
            // 4+ rows: first row → 0 (top), middle rows → 1, last row → 2 (bottom)
            if (reversedRow === 0) {
                return 0; // Top row
            } else if (reversedRow === totalRows - 1) {
                return 2; // Bottom row
            } else {
                return 1; // Middle rows
            }
        }
    };

    const getGlassSubPanelColIndex = (
        renderCol: number,
        totalCols: number
    ): number => {
        if (totalCols === 1) {
            return 1; // Middle position for single panel
        } else if (totalCols === 2) {
            // Two columns: first (left) → 0, second (right) → 2
            return renderCol === 0 ? 0 : 2;
        } else if (totalCols === 3) {
            // Three columns: direct mapping 0→0, 1→1, 2→2
            return renderCol;
        } else {
            // 4+ columns: first column → 0 (left), middle columns → 1, last column → 2 (right)
            if (renderCol === 0) {
                return 0; // Left column
            } else if (renderCol === totalCols - 1) {
                return 2; // Right column
            } else {
                return 1; // Middle columns
            }
        }
    };

    // Create cutouts for each panel (always create cutouts, even for glass panels)
    for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
            const x = xStart + xPanels.centers[Number(col)];
            const y = yStart + yPanels.centers[Number(row)];
            const w = xPanels.sizes[Number(col)];
            const h = yPanels.sizes[Number(row)];

            // Create cutout shape for this panel
            const cutoutShape = new THREE.Shape();
            cutoutShape.moveTo(x - w / 2, y - h / 2);
            cutoutShape.lineTo(x + w / 2, y - h / 2);
            cutoutShape.lineTo(x + w / 2, y + h / 2);
            cutoutShape.lineTo(x - w / 2, y + h / 2);
            cutoutShape.lineTo(x - w / 2, y - h / 2);

            // Add cutout to door shape
            doorShape.holes.push(cutoutShape);
        }
    }

    // Create extruded door geometry with cutouts
    const extrudeSettings = {
        depth: depth,
        bevelEnabled: true,
        bevelThickness,
        bevelSize,
        bevelSegments,
    };

    const doorGeometry = new THREE.ExtrudeGeometry(doorShape, {
        ...extrudeSettings,
        bevelEnabled: false,
    });
    const doorMaterial = new THREE.MeshStandardMaterial({
        color: frameColor,
    });
    const door = new THREE.Mesh(doorGeometry, doorMaterial);

    // Create backing panel with cutouts for glass subpanels
    const backingShape = new THREE.Shape();
    // Start with outer backing panel boundary
    backingShape.moveTo(-width / 2, -height / 2);
    backingShape.lineTo(width / 2, -height / 2);
    backingShape.lineTo(width / 2, height / 2);
    backingShape.lineTo(-width / 2, height / 2);
    backingShape.lineTo(-width / 2, -height / 2);

    // Create cutouts in backing panel for glass subpanels only
    if (glassSubPanels) {
        for (let row = 0; row < rows; row++) {
            for (let col = 0; col < cols; col++) {
                const arrayRow = getGlassSubPanelRowIndex(row, rows);
                const arrayCol = getGlassSubPanelColIndex(col, cols);
                if (
                    arrayRow >= 0 &&
                    arrayRow < glassSubPanels.length &&
                    Array.isArray(glassSubPanels[Number(arrayRow)]) &&
                    arrayCol >= 0 &&
                    arrayCol < glassSubPanels[Number(arrayRow)].length &&
                    glassSubPanels[Number(arrayRow)][Number(arrayCol)] === true
                ) {
                    // This is a glass subpanel - create cutout in backing panel
                    const x = xStart + xPanels.centers[Number(col)];
                    const y = yStart + yPanels.centers[Number(row)];
                    const w = xPanels.sizes[Number(col)];
                    const h = yPanels.sizes[Number(row)];

                    const backingCutoutShape = new THREE.Shape();
                    backingCutoutShape.moveTo(x - w / 2, y - h / 2);
                    backingCutoutShape.lineTo(x + w / 2, y - h / 2);
                    backingCutoutShape.lineTo(x + w / 2, y + h / 2);
                    backingCutoutShape.lineTo(x - w / 2, y + h / 2);
                    backingCutoutShape.lineTo(x - w / 2, y - h / 2);

                    // Add cutout to backing shape
                    backingShape.holes.push(backingCutoutShape);
                }
            }
        }
    }

    // Create extruded backing panel geometry with cutouts
    const backingExtrudeSettings = {
        depth: 1, // 1mm thick
        bevelEnabled: false,
    };
    const backingGeometry = new THREE.ExtrudeGeometry(
        backingShape,
        backingExtrudeSettings
    );
    const backingMaterial = new THREE.MeshStandardMaterial({
        color: frameColor, // Same color as door frame
    });
    const backingPanel = new THREE.Mesh(backingGeometry, backingMaterial);
    door.add(backingPanel);

    // Create panels to fill the cutouts
    for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
            const x = xStart + xPanels.centers[Number(col)];
            const y = yStart + yPanels.centers[Number(row)];
            const w = xPanels.sizes[Number(col)];
            const h = yPanels.sizes[Number(row)];

            // Check if this is a glass subpanel
            const isGlassSubPanel =
                glassSubPanels &&
                (() => {
                    const arrayRow = getGlassSubPanelRowIndex(row, rows);
                    const arrayCol = getGlassSubPanelColIndex(col, cols);
                    return (
                        arrayRow >= 0 &&
                        arrayRow < glassSubPanels.length &&
                        Array.isArray(glassSubPanels[Number(arrayRow)]) &&
                        arrayCol >= 0 &&
                        arrayCol < glassSubPanels[Number(arrayRow)].length &&
                        glassSubPanels[Number(arrayRow)][Number(arrayCol)] ===
                            true
                    );
                })();

            // For glass subpanels, create line outline at the cutout edges (door frame level)
            if (isGlassSubPanel) {
                // Lines should follow the cutout edges at the full panel size
                // Create line segments for the cutout rectangle at both front and back of door frame
                const frontDepth = depth; // At the front face of the door where cutout is
                const backDepth = 0; // At the back face of the door where cutout is

                // Helper function to create cutout outline points at a specific depth
                const createCutoutOutline = (zDepth: number) => [
                    // Bottom edge
                    new THREE.Vector3(x - w / 2, y - h / 2, zDepth),
                    new THREE.Vector3(x + w / 2, y - h / 2, zDepth),
                    // Right edge
                    new THREE.Vector3(x + w / 2, y - h / 2, zDepth),
                    new THREE.Vector3(x + w / 2, y + h / 2, zDepth),
                    // Top edge
                    new THREE.Vector3(x + w / 2, y + h / 2, zDepth),
                    new THREE.Vector3(x - w / 2, y + h / 2, zDepth),
                    // Left edge
                    new THREE.Vector3(x - w / 2, y + h / 2, zDepth),
                    new THREE.Vector3(x - w / 2, y - h / 2, zDepth),
                ];

                // Create front outline
                const frontCutoutPoints = createCutoutOutline(frontDepth);
                const frontCutoutGeometry =
                    new THREE.BufferGeometry().setFromPoints(frontCutoutPoints);
                const outlineMaterial = new THREE.LineBasicMaterial({
                    color: '#333',
                });
                const frontOutline = new THREE.LineSegments(
                    frontCutoutGeometry,
                    outlineMaterial
                );
                frontOutline.renderOrder = 1;
                door.add(frontOutline);

                // Create back outline
                const backCutoutPoints = createCutoutOutline(backDepth);
                const backCutoutGeometry =
                    new THREE.BufferGeometry().setFromPoints(backCutoutPoints);
                const backOutline = new THREE.LineSegments(
                    backCutoutGeometry,
                    outlineMaterial
                );
                backOutline.renderOrder = 1;
                door.add(backOutline);

                continue;
            }

            // Create main panel shape - smaller than cutout to show bevel border
            // For collective faces, panels fill the entire cutout without bevel
            const borderSize = isCollectiveFace ? 0 : 10; // Border size to show bevel
            const actualPanelWidth = Math.max(0, w - borderSize * 2);
            const actualPanelHeight = Math.max(0, h - borderSize * 2);

            const panelShape = new THREE.Shape();
            panelShape.moveTo(-actualPanelWidth / 2, -actualPanelHeight / 2);
            panelShape.lineTo(actualPanelWidth / 2, -actualPanelHeight / 2);
            panelShape.lineTo(actualPanelWidth / 2, actualPanelHeight / 2);
            panelShape.lineTo(-actualPanelWidth / 2, actualPanelHeight / 2);
            panelShape.lineTo(-actualPanelWidth / 2, -actualPanelHeight / 2);

            if (isCollectiveFace) panelDepth = depth;

            const panelExtrudeSettings = {
                depth: panelDepth, // Extrusion depth (maintained regardless of bevel state)
                bevelEnabled: !isCollectiveFace, // Disable bevel for collective faces
                bevelThickness: -bevelThickness, // Negative bevel thickness for inward bevel
                bevelSize,
                bevelSegments,
            };

            const panelGeometry = new THREE.ExtrudeGeometry(
                panelShape,
                panelExtrudeSettings
            );
            const panelMaterial = new THREE.MeshStandardMaterial({
                color: panelColor,
            });
            const panel = new THREE.Mesh(panelGeometry, panelMaterial);

            // Position panel inside the cutout - recessed into door frame
            panel.position.x = x;
            panel.position.y = y;
            panel.position.z = depth - panelDepth + 1;

            // Add panel to door with line segments
            const panelSegments = createLineSegments(panel);
            panelSegments.renderOrder = 1; // Render on top to prevent disappearing

            // For collective faces, hide the panel but keep the lines visible
            if (isCollectiveFace) {
                panel.visible = false;
                // Add line segments directly to door so they remain visible
                panelSegments.position.x = x;
                panelSegments.position.y = y;
                panelSegments.position.z = depth - panelDepth + 1;
                door.add(panelSegments);
            } else {
                panel.add(panelSegments);
            }
            door.add(panel);
        }
    }

    // Add line segments for door edges (only outer boundary, no cutout lines)
    const hinge = new THREE.Group();
    const doorSegments = createDoorBoundaryLines(door, width, height, depth);
    door.add(doorSegments);
    hinge.add(door);

    if (isTopHangDoor) {
        door.position.x -= width / 2;
        door.position.y += height / 2;
    } else door.position.x = isRightDoor ? -width / 2 : width / 2;

    return hinge;
};

/**
 * Fades the door's outlines to match the cabinet panel outlines. Lines stay
 * fully opaque while the faces aren't drawn (wireframe mode), and follow
 * later wireframe toggles via onToggleWireframe.
 * @param {THREE.Object3D} doorModel Shaker door model.
 * @param {boolean} showTexture Whether the door faces are drawn.
 */
export const applyShakerDoorLineTransparency = (
    doorModel: THREE.Object3D,
    showTexture: boolean
) => {
    doorModel.traverse((child) => {
        if (!(child instanceof THREE.LineSegments)) return;
        if (!(child.material instanceof THREE.LineBasicMaterial)) return;

        const lineSegments = child as THREE.LineSegments<
            THREE.BufferGeometry,
            THREE.LineBasicMaterial
        >;
        setSegmentLineTransparency(lineSegments.material, !showTexture);
        attachSegmentLineWireframeToggle(lineSegments);
    });
};

export const createLineSegments = (mesh: THREE.Mesh) => {
    const edges = new THREE.EdgesGeometry(mesh.geometry);

    const lineMaterial = new THREE.LineBasicMaterial({
        color: '#333',
    });

    addDragEffectOnClick(
        mesh as unknown as THREE.Mesh<
            THREE.BoxGeometry,
            THREE.MeshStandardMaterial,
            THREE.Object3DEventMap
        >,
        lineMaterial
    );

    return new THREE.LineSegments(edges, lineMaterial);
};

// Helper function to filter edges and remove interior cutout edges
const createDoorBoundaryLines = (
    mesh: THREE.Mesh,
    width: number,
    height: number,
    depth: number
) => {
    const halfWidth = width / 2;
    const halfHeight = height / 2;

    // Create positions for complete door box outline
    // Note: ExtrudeGeometry extrudes from z=0 to z=depth, so front face is at z=depth, back at z=0
    const positions = [
        // Front face (z=depth)
        // Top edge
        -halfWidth,
        halfHeight,
        depth,
        halfWidth,
        halfHeight,
        depth,
        // Right edge
        halfWidth,
        halfHeight,
        depth,
        halfWidth,
        -halfHeight,
        depth,
        // Bottom edge
        halfWidth,
        -halfHeight,
        depth,
        -halfWidth,
        -halfHeight,
        depth,
        // Left edge
        -halfWidth,
        -halfHeight,
        depth,
        -halfWidth,
        halfHeight,
        depth,
        // Back face (z=0)
        // Top edge
        -halfWidth,
        halfHeight,
        0,
        halfWidth,
        halfHeight,
        0,
        // Right edge
        halfWidth,
        halfHeight,
        0,
        halfWidth,
        -halfHeight,
        0,
        // Bottom edge
        halfWidth,
        -halfHeight,
        0,
        -halfWidth,
        -halfHeight,
        0,
        // Left edge
        -halfWidth,
        -halfHeight,
        0,
        -halfWidth,
        halfHeight,
        0,
        // Connecting edges (front to back)
        // Top-left
        -halfWidth,
        halfHeight,
        depth,
        -halfWidth,
        halfHeight,
        0,
        // Top-right
        halfWidth,
        halfHeight,
        depth,
        halfWidth,
        halfHeight,
        0,
        // Bottom-right
        halfWidth,
        -halfHeight,
        depth,
        halfWidth,
        -halfHeight,
        0,
        // Bottom-left
        -halfWidth,
        -halfHeight,
        depth,
        -halfWidth,
        -halfHeight,
        0,
    ];

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute(
        'position',
        new THREE.Float32BufferAttribute(positions, 3)
    );

    const lineMaterial = new THREE.LineBasicMaterial({color: '#333'});

    addDragEffectOnClick(
        mesh as unknown as THREE.Mesh<
            THREE.BoxGeometry,
            THREE.MeshStandardMaterial,
            THREE.Object3DEventMap
        >,
        lineMaterial
    );

    return new THREE.LineSegments(geometry, lineMaterial);
};

export const setupDoorAnimationDefault = ({
    doorModel,
    isRightDoor,
    initiallyOpen,
    origin = 'PRODUCT_VISUALISER',
    variables,
    isCornerLeftDoor,
    isCornerRightDoor,
    isTopHangDoor,
    isDrawerFront,
    isLeftApplianceMainDoor,
    isLeftApplianceExtendedDoor,
    isRightApplianceMainDoor,
    isRightApplianceExtendedDoor,
    isDoorPullout,
    isPairDoor = false,
    carcaseThickness,
    exteriorThickness,
    values,
    drawerIndex = 0,
}: {
    doorModel: THREE.Group;
    isRightDoor: boolean;
    initiallyOpen: boolean;
    origin?: Origin;
    variables?: TemplateVariable;
    isCornerLeftDoor?: boolean;
    isCornerRightDoor?: boolean;
    isTopHangDoor?: boolean;
    isDrawerFront?: boolean;
    isLeftApplianceMainDoor?: boolean;
    isLeftApplianceExtendedDoor?: boolean;
    isRightApplianceMainDoor?: boolean;
    isRightApplianceExtendedDoor?: boolean;
    isDoorPullout?: boolean;
    isPairDoor?: boolean;
    carcaseThickness?: number;
    exteriorThickness?: number;
    values?: PreviewFormValues;
    drawerIndex?: number;
}): void => {
    let degrees =
        origin === 'ROOM_PLANNER'
            ? !!variables?.innerDrawerCount || variables?.isReturnProduct
                ? 90
                : 65
            : DOOR_OPEN_DEGREES;

    if (isLeftApplianceMainDoor) {
        degrees = 87;
    }

    let animationHandler =
        isCornerRightDoor || isCornerLeftDoor
            ? toggleCornerHandler({
                  mesh: doorModel,
                  isRightDoor: Boolean(isCornerRightDoor),
              })
            : createShakerDoorAnimationHandler(
                  doorModel,
                  isRightDoor,
                  degrees,
                  isTopHangDoor
              );

    const {isBinProduct, isBinWithInnerDrawerProduct} = variables;
    const isBin = isBinProduct || isBinWithInnerDrawerProduct;
    const drawerOpenOffset = isBin
        ? -400
        : isDoorPullout
          ? DOOR_PULLOUT_OPEN_OFFSET
          : -Math.min(50 * Math.abs(drawerIndex + 1), 200);

    if (isDrawerFront || isDoorPullout) {
        animationHandler = toggleDrawerHandler({
            mesh: doorModel,
            drawerIndex,
            openDelay: 0,
            ...(isBin
                ? {offset: -400}
                : isDoorPullout
                  ? {offset: DOOR_PULLOUT_OPEN_OFFSET}
                  : {}),
        });
    }

    if (isLeftApplianceExtendedDoor) {
        animationHandler = toggleApplianceExtendedDoorHandler({
            mesh: doorModel,
            variables,
            values,
            carcaseThickness,
            origin,
            exteriorThickness,
        });
    }

    if (isRightApplianceExtendedDoor) {
        animationHandler = toggleApplianceExtendedDoorHandler({
            mesh: doorModel,
            variables,
            values,
            carcaseThickness,
            isRightExtendedDoor: true,
            origin,
            exteriorThickness,
        });
    }

    if (isRightApplianceMainDoor) {
        animationHandler = createShakerDoorAnimationHandler(
            doorModel,
            false,
            87,
            isTopHangDoor,
            true
        );
    }

    // Vinyl / shaker return doors: `hingeFixup` is set to be 'none' because
    // `calculateDoorXPositionDefault` already offsets the shaker group for
    // `returnPanelWidth` at build time.
    //
    // The shaker group origin is the hinge edge, so the built `position.x` is
    // the hinge axis in both the Room Planner and 3D Preview frames.
    // `computeReturnDoorAxis` ignores the pair split, door gap and void width
    // that `calculateDoorXPositionDefault` applies, so the close snap would
    // move the door off its built position.
    if (
        variables?.isReturnProduct &&
        !isCornerLeftDoor &&
        !isCornerRightDoor &&
        !isTopHangDoor &&
        !isDrawerFront &&
        !isDoorPullout &&
        !isLeftApplianceMainDoor &&
        !isLeftApplianceExtendedDoor &&
        !isRightApplianceMainDoor &&
        !isRightApplianceExtendedDoor
    ) {
        const returnDoorHandler = createReturnDoorAnimationHandler({
            mesh: doorModel,
            variables,
            values,
            isRight: !!isRightDoor,
            isPairDoor,
            carcaseThickness: carcaseThickness ?? 0,
            hingeFixup: 'none',
            axisPosition: doorModel.position.x,
        });
        if (returnDoorHandler) animationHandler = returnDoorHandler;
    }

    doorModel.userData.boundsExcludeAnimatedDoorOrDrawerPart =
        Boolean(animationHandler);

    doorModel.userData.toggleDoor = async ({
        isDoorOrDrawerOpen,
        animationSpeed,
    }: {
        isDoorOrDrawerOpen: boolean;
        animationSpeed?: number;
    }): Promise<void> => {
        if (!animationHandler) return;

        const {open, close} = animationHandler;

        if (isDoorOrDrawerOpen) {
            if (!open.length) return;

            // Run all open animations concurrently and await completion
            await Promise.all(
                open.map((openAnimation) => openAnimation(animationSpeed, true))
            );
            return;
        }

        if (!close.length) return;

        // Run all close animations concurrently and await completion
        await Promise.all(
            close.map((closeAnimation) => closeAnimation(animationSpeed))
        );
    };

    // Kept so callers that measure the door can await the pose.
    doorModel.userData.initialPoseSettled = (
        doorModel.userData as {
            toggleDoor: ({
                isDoorOrDrawerOpen,
                animationSpeed,
            }: {
                isDoorOrDrawerOpen: boolean;
                animationSpeed?: number;
            }) => Promise<void>;
        }
    )?.toggleDoor({
        isDoorOrDrawerOpen: initiallyOpen,
        animationSpeed: 0,
    });

    if (initiallyOpen && (isDrawerFront || isDoorPullout)) {
        // Setting the initial position to prevent flickering when animating at 0 speed
        doorModel.position.setY(drawerOpenOffset);
    }
};

// The initial pose is a zero-length TWEEN, so it only lands on the next
// TWEEN.update(). Bounds read before that belong to a closed door.
export const waitForInitialDoorPoses = async (
    doors: THREE.Object3D[],
    timeoutMs = INITIAL_DOOR_POSE_TIMEOUT_MS
): Promise<void> => {
    const pending: Promise<unknown>[] = [];

    doors.forEach((door) =>
        door.traverse((object) => {
            const {initialPoseSettled} = object.userData as {
                initialPoseSettled?: Promise<unknown>;
            };
            if (initialPoseSettled != null) pending.push(initialPoseSettled);
        })
    );

    if (!pending.length) return;

    let timer: ReturnType<typeof setTimeout>;

    await Promise.race([
        Promise.allSettled(pending),
        new Promise((resolve) => {
            timer = setTimeout(resolve, timeoutMs);
        }),
    ]);

    clearTimeout(timer);
};

const createShakerDoorAnimationHandler = (
    mesh: THREE.Group,
    isRightDoor = false,
    degrees = 120,
    isTopHangDoor = false,
    isAppliance = false
): AnimationHandler => {
    const radians = (degrees * Math.PI) / 180;

    let initialRotation = isTopHangDoor ? {x: (90 * Math.PI) / 180} : {y: 0};

    let openRotation = isTopHangDoor
        ? {x: -(17 * Math.PI) / 180}
        : {y: isRightDoor ? radians : -radians};
    let closeRotation = isTopHangDoor ? {x: (90 * Math.PI) / 180} : {y: 0};

    if (isAppliance) {
        mesh.scale.z = -1;

        const applianceStart = Math.PI;
        const applianceOpen = applianceStart + (87 * Math.PI) / 180;

        initialRotation = {y: applianceStart};
        openRotation = {y: applianceOpen};
        closeRotation = {y: applianceStart};

        mesh.rotation.y = applianceStart;
    }

    const openAnimation = (animationSpeed = 1000) =>
        new Promise<void>((resolve) => {
            const openTween = new TWEEN.Tween(
                initialRotation,
                USE_LEGACY_TWEEN_GROUP
            )
                .to(openRotation, animationSpeed)
                .easing(TWEEN.Easing.Quadratic.Out)
                .onComplete(() => {
                    resolve();
                })
                .onUpdate(() => {
                    if (isTopHangDoor) mesh.rotation.x = initialRotation.x;
                    else mesh.rotation.y = initialRotation.y;
                });

            openTween.start();
        });

    const closeAnimation = (animationSpeed = 1000) =>
        new Promise<void>((resolve) => {
            // Set up the rotation animation
            const closeTween = new TWEEN.Tween(
                initialRotation,
                USE_LEGACY_TWEEN_GROUP
            )
                .to(closeRotation, animationSpeed)
                .easing(TWEEN.Easing.Quadratic.Out)
                .onComplete(() => {
                    resolve();
                })
                .onUpdate(() => {
                    if (isTopHangDoor) mesh.rotation.x = initialRotation.x;
                    else mesh.rotation.y = initialRotation.y;
                });

            closeTween.start();
        });

    return {
        open: [openAnimation],
        close: [closeAnimation],
    };
};

export const toggleCornerHandler = ({
    mesh,
    isRightDoor = false,
}: {
    mesh: THREE.Group<THREE.Object3DEventMap>;
    isRightDoor: boolean;
}): AnimationHandler => {
    const boundingBox = new THREE.Box3().setFromObject(mesh);
    const size = new THREE.Vector3();
    boundingBox.getSize(size);

    const degrees = 90;
    const radians = (degrees * Math.PI) / 180;

    const initialRotation = {y: (45 * Math.PI) / 180};
    const openRotation = {
        y: isRightDoor ? (170 * Math.PI) / 180 : -radians,
    };
    const closeRotation = {y: (45 * Math.PI) / 180};

    const openCornerDoor = (animationSpeed = 1000) =>
        new Promise<void>((resolve) => {
            const openTween = new TWEEN.Tween(
                initialRotation,
                USE_LEGACY_TWEEN_GROUP
            )
                .to(openRotation, animationSpeed)
                .easing(TWEEN.Easing.Quadratic.Out)
                .onComplete(() => {
                    resolve();
                })
                .onUpdate(() => {
                    mesh.rotation.y = initialRotation.y;
                });

            openTween.start();
        });

    const closeCornerDoor = (animationSpeed = 1000) =>
        new Promise<void>((resolve) => {
            const close = new TWEEN.Tween(
                initialRotation,
                USE_LEGACY_TWEEN_GROUP
            )
                .to(closeRotation, animationSpeed)
                .easing(TWEEN.Easing.Quadratic.Out)
                .onComplete(() => {
                    resolve();
                })
                .onUpdate(() => {
                    mesh.rotation.y = initialRotation.y;
                });

            close.start();
        });

    return {
        open: [openCornerDoor],
        close: [closeCornerDoor],
    };
};

export const toggleDrawerHandler = ({
    mesh,
    offset,
    openDelay,
    drawerIndex = 0,
}: {
    mesh: THREE.Group<THREE.Object3DEventMap>;
    offset?: number;
    openDelay?: number;
    drawerIndex?: number;
}): AnimationHandler => {
    const depthOffsetCalculation = Math.min(
        75 * Math.abs(drawerIndex + 1) + (drawerIndex === 0 ? 25 : 0),
        200
    );

    const resolvedOffset = offset ?? -depthOffsetCalculation;

    const initialPosition = {
        y: mesh.position.y,
    };

    const openPosition = {
        y: mesh.position.y + resolvedOffset,
    };

    const closePosition = {
        y: mesh.position.y,
    };

    const openDrawer = (animationSpeed = 600) =>
        new Promise<void>((resolve) => {
            const tween = new TWEEN.Tween(
                initialPosition,
                USE_LEGACY_TWEEN_GROUP
            )
                .to(openPosition, animationSpeed)
                .delay(openDelay)
                .easing(TWEEN.Easing.Quadratic.Out)
                .onComplete(() => {
                    resolve();
                })
                .onUpdate(() => {
                    mesh.position.y = initialPosition.y;
                });
            tween.start();
        });

    const closeTween = (animationSpeed = 600) =>
        new Promise<void>((resolve) => {
            const tween = new TWEEN.Tween(
                initialPosition,
                USE_LEGACY_TWEEN_GROUP
            )
                .to(closePosition, animationSpeed)
                .easing(TWEEN.Easing.Quadratic.Out)
                .onComplete(() => {
                    resolve();
                })
                .onUpdate(() => {
                    mesh.position.y = initialPosition.y;
                });

            tween.start();
        });

    return {
        open: [openDrawer],
        close: [closeTween],
    };
};

export const toggleApplianceExtendedDoorHandler = ({
    mesh,
    variables,
    values,
    carcaseThickness,
    isRightExtendedDoor = false,
    origin = 'PRODUCT_VISUALISER',
    exteriorThickness = 16.5,
}: {
    mesh: THREE.Group<THREE.Object3DEventMap>;
    variables: TemplateVariable;
    values: PreviewFormValues;
    carcaseThickness: number;
    isRightExtendedDoor?: boolean;
    origin?: Origin;
    exteriorThickness?: number;
}): AnimationHandler => {
    const openingAngle = 87;
    const boundingBox = new THREE.Box3().setFromObject(mesh);
    const size = new THREE.Vector3();
    boundingBox.getSize(size);

    const width = size.x;
    const isProductVisualiser = origin === 'PRODUCT_VISUALISER';
    const meshPositionX = width * 2 + exteriorThickness * 0.2;

    if (isRightExtendedDoor) {
        mesh.position.x = isProductVisualiser
            ? -width
            : variables?.isApplianceLeftRightBifold
              ? width
              : 0;
        if (!isProductVisualiser && variables?.isApplianceBifoldPair) {
            mesh.position.x = meshPositionX;
        }
    } else {
        mesh.position.x = isProductVisualiser ? width / 2 : meshPositionX;
        mesh.scale.x = -1;
    }

    const base = calculateBaseWithAngle(width, openingAngle);

    const radians = (openingAngle * Math.PI) / 180;
    const initialRotation = {y: 0};
    const openRotation = {y: isRightExtendedDoor ? -radians : radians};
    const closeRotation = {y: 0};

    const openDoor = (animationSpeed = 600) =>
        new Promise<void>((resolve) => {
            const openTween = new TWEEN.Tween(
                initialRotation,
                USE_LEGACY_TWEEN_GROUP
            )
                .to(openRotation, animationSpeed)
                .easing(TWEEN.Easing.Quadratic.Out)
                .onComplete(() => {
                    resolve();
                })
                .onUpdate(() => {
                    mesh.rotation.y = initialRotation.y;
                });

            openTween.start();
        });

    const closeTween = (animationSpeed = 1000) =>
        new Promise<void>((resolve) => {
            const tween = new TWEEN.Tween(
                initialRotation,
                USE_LEGACY_TWEEN_GROUP
            )
                .to(closeRotation, animationSpeed)
                .easing(TWEEN.Easing.Quadratic.Out)
                .onComplete(() => {
                    resolve();
                })
                .onUpdate(() => {
                    mesh.rotation.y = initialRotation.y;
                });

            tween.start();
        });

    const cabinetWidth = Number(values.cabinet_width) + carcaseThickness;
    const doorGap = Number(values.cabinet_door_gap);
    const threeDoorWidth = (cabinetWidth - doorGap * 2) / 3;

    const position = {
        x: variables?.isApplianceLeftBifoldRight
            ? threeDoorWidth * 2 + doorGap + 2
            : isRightExtendedDoor
              ? variables?.isApplianceLeftRightBifold
                  ? threeDoorWidth + doorGap
                  : -1
              : cabinetWidth - Number(values.cabinet_door_right) + 1,
    };

    let initialPosition = {...position};
    const openPosition = {
        x: isRightExtendedDoor
            ? cabinetWidth - base * 2 - Number(values.cabinet_door_right)
            : base * 2 + Number(values.cabinet_door_left),
    };

    let closePosition = {...position};

    if (variables?.isApplianceBifoldPair) {
        const fourDoorWidth = (cabinetWidth - doorGap * 3) / 4;

        if (isRightExtendedDoor) {
            const positionRight = {x: fourDoorWidth * 2 + doorGap * 2 - 2};
            initialPosition = {x: mesh.position.x};
            closePosition = {...positionRight};
        } else {
            const positionRight = {x: fourDoorWidth * 2 + doorGap};
            initialPosition = {...positionRight};
            closePosition = {...positionRight};
        }
    }

    if (isProductVisualiser) {
        if (values.cabinet_width) {
            initialPosition.x -= values.cabinet_width / 2;
            closePosition.x -= values.cabinet_width / 2;
            openPosition.x -= values.cabinet_width / 2;
        }
    }

    const openDoorX = (animationSpeed = 1000) =>
        new Promise<void>((resolve) => {
            const openTween = new TWEEN.Tween(
                initialPosition,
                USE_LEGACY_TWEEN_GROUP
            )
                .to(openPosition, animationSpeed)
                .easing(TWEEN.Easing.Quadratic.Out)
                .onComplete(() => {
                    resolve();
                })
                .delay(0)
                .onUpdate(() => {
                    mesh.position.x = initialPosition.x;
                });

            openTween.start();
        });

    const closeTween2 = (animationSpeed = 600) =>
        new Promise<void>((resolve) => {
            const tween = new TWEEN.Tween(
                initialPosition,
                USE_LEGACY_TWEEN_GROUP
            )
                .to(closePosition, animationSpeed)
                .easing(TWEEN.Easing.Quadratic.Out)
                .onComplete(() => {
                    resolve();
                })
                .onUpdate(() => {
                    mesh.position.x = initialPosition.x;
                });

            tween.start();
        });

    return {
        open: [openDoor, openDoorX],
        close: [closeTween, closeTween2],
    };
};

const calculateBaseWithAngle = (
    hypotenuse: number,
    angleInDegrees: number
): number => {
    const angleInRadians = angleInDegrees * (Math.PI / 180);
    const base = hypotenuse * Math.cos(angleInRadians);
    return base;
};
